<script>
  import { onMount } from "svelte";
  import { useNavigate } from "svelte-navigator";
  import {
    PasswordInput,
    TextInput,
    Button,
  } from "paxray-components-svelte";
  import Header from "lib/components/Header.svelte";
  import SideNavHeaderLogo from "lib/components/layout/sideNav/SideNavHeaderLogo.svelte";
  import Modal from "lib/components/Modal.svelte";
  import ContentSwitcher from "lib/components/ContentSwitcher.svelte";
  import FrameworkAssistantProgress from "lib/components/paxbot/FrameworkAssistantProgress.svelte";
  import { ROUTES } from "@utils/routes.js";
  import { fetchWrapper } from "lib/util/fetchWrapper.js";
  import { EDIT_ICON, TRASH_ICON } from '@utils/icons.js';

  const navigate = useNavigate();

  const steps = [
    "Get started",
    "Configure server",
    "Add admins",
    "Confirm setup",
  ];

  let currentStep = 0;
  let setupCompleted = false;

  let currentUser = {};
  let testUserModal = {
    username: false,
    email: false,
    password: false,
    confirmPassword: false,
    save: false,
  };

  let passwordType = "password";
  let confirmPasswordType = "password";

  let users = [];

  let userModalOpen = false;
  let deleteUserModalOpen = false;
  let userToDelete = null;
  let hostname = "/";

  $: if (currentStep == 2) getUsers();

  // Bereits erledigte Schritte bleiben im Stepper anklickbar, nach Abschluss nicht mehr
  $: navigableSteps = setupCompleted
    ? []
    : steps.map((_, index) => index).filter((index) => index < currentStep);

  $: incorrectPassword = currentUser.password !== currentUser.passwordConfirm;

  $: disablePasswordInput =
    (incorrectPassword && testUserModal.save) ||
    (!currentUser.id && testUserModal.password && !currentUser.password) ||
    (testUserModal.password &&
      testUserModal.confirmPassword &&
      !currentUser.password &&
      currentUser.passwordConfirm);

  $: disableConfirmPasswordInput =
    (incorrectPassword && testUserModal.save) ||
    (!currentUser.id &&
      testUserModal.confirmPassword &&
      !currentUser.passwordConfirm) ||
    (testUserModal.confirmPassword &&
      testUserModal.password &&
      currentUser.password &&
      !currentUser.passwordConfirm);

  $: saveUserNotAllowed =
    !currentUser.username ||
    !currentUser.email ||
    (currentUser.id && !currentUser.password && currentUser.passwordConfirm) ||
    (currentUser.password && !currentUser.passwordConfirm) ||
    (!currentUser.id && (!currentUser.password || !currentUser.passwordConfirm));

  function getUsers() {
    fetchWrapper(`api/setup/users`)
      .then((res) => res.json())
      .then((data) => {
        users = data.data;
      });
  }

  function resetUserModal() {
    confirmPasswordType = "password";
    passwordType = "password";
    testUserModal = {
      username: false,
      email: false,
      password: false,
      confirmPassword: false,
      save: false,
    };
  }

  function editUser(id) {
    if (id != 0) {
      currentUser = { ...users.find((x) => x.id == id) };
    } else
      currentUser = {
        id: 0,
        username: "",
        email: "",
      };
    currentUser.password = "";
    currentUser.passwordConfirm = "";
    resetUserModal();
    userModalOpen = true;
  }

  function closeUserModal() {
    currentUser.password = "";
    currentUser.passwordConfirm = "";
    userModalOpen = false;
  }

  function saveUser() {
    if (saveUserNotAllowed) return;
    if (incorrectPassword) {
      testUserModal.save = true;
      return;
    }
    let editUser = currentUser.id;
    let method = editUser ? "PATCH" : "POST";
    let url = editUser
      ? `/api/setup/users/${currentUser.id}`
      : `/api/setup/users`;

    let userToSave = {
      email: currentUser.email,
      username: currentUser.username,
      password: currentUser.password,
    };
    fetchWrapper(url, {
      method: method,
      body: JSON.stringify(userToSave),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.ok) {
          userModalOpen = false;
          if (!editUser) {
            users.push(data.data);
          } else {
            users[users.findIndex((x) => x.id == currentUser.id)] = {
              ...currentUser,
            };
          }
          users = users;
        }
        currentUser.password = "";
        currentUser.passwordConfirm = "";
      });
  }

  function deleteUser(id) {
    fetchWrapper(`/api/setup/users/${id}`, {
      method: "DELETE",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.ok) {
          users = users.filter((x) => x.id != id);
        }
        deleteUserModalOpen = false;
        userToDelete = null;
      });
  }

  function setupComplete() {
    fetchWrapper(`/api/setup/setup-complete`, {
      method: "POST",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.ok) {
          navigate(ROUTES.home);
        }
      });
  }

  // ContentSwitcher arbeitet mit dem Index der Option: 0 = HTTPS, 1 = HTTP
  // Vorauswahl wie die Seite gerade aufgerufen wird
  let protocolIndex = window.location.protocol == "https:" ? 0 : 1;
  $: httpsenabled = protocolIndex === 0;
  let serverUrl = window.location.host;
  let serverPort = window.location.port;

  function setServerDetails() {
    fetchWrapper(`/api/setup/set-server-details`, {
      method: "POST",
      body: JSON.stringify({
        httpsenabled: httpsenabled,
        serverUrl: serverUrl,
        serverPort: serverPort,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.ok) {
          currentStep = 2;
        }
      });
  }

  onMount(() => {
    fetchWrapper(`/api/setup-info`)
      .then((res) => res.json())
      .then((data) => {
        hostname = data.hostname;
        if (data.setupcomplete) {
          setupCompleted = true;
          currentStep = steps.length - 1;
        }
      });
  });
</script>

<div class="setup">
  <div class="setup-logo">
    <SideNavHeaderLogo full />
  </div>
  <Header section="" breadcrumb="Initial Setup" showTooltip={false}>
    <div class="setup-header-progress">
      <FrameworkAssistantProgress
        {steps}
        {currentStep}
        completed={setupCompleted}
        {navigableSteps}
        ariaLabel="Setup progress"
        on:navigate={(event) => (currentStep = event.detail)}
      />
    </div>
  </Header>

  <main class="setup-content">
    <section class="setup-panel">
      {#if setupCompleted}
        <h1>Setup already completed</h1>
        <p class="setup-subtitle">
          This installation is ready. You can sign in now.
        </p>
        <div class="setup-actions setup-actions--end">
          <Button
            size="small"
            kind="secondary"
            text="Continue to sign in"
            on:click={() => navigate(hostname)}
          />
        </div>
      {:else if currentStep === 0}
        <h1>Welcome to paxray</h1>
        <p class="setup-subtitle">
          Thank you for installing the paxray Analytics Suite. A few short steps
          prepare this installation for your team.
        </p>
        <div class="setup-actions setup-actions--end">
           <Button
            size="small"
            kind="secondary"
            text="Let's get started"
            on:click={() => (currentStep = 1)}
          />
        </div>
      {:else if currentStep === 1}
        <h1>Server details</h1>
        <p class="setup-subtitle">
          Enter how this server is reached. HTTPS is recommended for security
          and privacy.
        </p>
        <div class="setup-form">
          <div class="setup-field">
            <span class="bx--label">Protocol</span>
            <ContentSwitcher
              options={["HTTPS", "HTTP"]}
              bind:selected={protocolIndex}
            />
            <p class="setup-hint">
              Choose HTTPS if this server has an SSL certificate.
            </p>
          </div>
          <TextInput labelText="Server URL*" bind:value={serverUrl} />
          <TextInput labelText="Server Port" bind:value={serverPort} />
        </div>
        <div class="setup-actions">
           <Button
            size="small"
            kind="secondary"
            text="Back"
            on:click={() => (currentStep = 0)}
          />
          <Button
            size="small"
            kind="secondary"
            text="Next"
            notAllowed={!serverUrl}
            on:click={setServerDetails}
          />
        </div>
      {:else if currentStep === 2}
        <div class="setup-title-row">
          <h1>Admin users</h1>
          <Button
            kind="secondary"
            text="Add user"
            size="small"
            on:click={() => editUser(0)}
          />
        </div>
        <p class="setup-subtitle">
          Add at least one admin user. All other users can be added later in
          the settings.
        </p>
        <div class="setup-user-list">
          {#each users as user (user.id)}
            <div class="setup-user">
              <div class="setup-user__text">
                <span class="setup-user__name">{user.username}</span>
                {#if user.email}
                  <span class="setup-user__email">{user.email}</span>
                {/if}
              </div>
              <div class="setup-user__actions">
                <Button
                  icon={EDIT_ICON}
                  size="field"
                  kind="secondary"
                  iconDescription="Edit user"
                  on:click={() => editUser(user.id)}
                />
                <Button
                  icon={TRASH_ICON}
                  size="field"
                  kind="secondary"
                  iconDescription="Delete user"
                  on:click={() => {
                    userToDelete = user;
                    deleteUserModalOpen = true;
                  }}
                />
              </div>
            </div>
          {:else}
            <p class="setup-empty">No admin users yet</p>
          {/each}
        </div>
        <div class="setup-actions">
          <Button
            size="small"
            kind="secondary"
            text="Back"
            on:click={() => (currentStep = 1)}
          />
          <Button
            size="small"
            kind="secondary"
            text="Next"
            notAllowed={!users.length}
            on:click={() => (currentStep = 3)}
          />
        </div>
      {:else if currentStep === 3}
        <h1>Ready to go</h1>
        <p class="setup-subtitle">
          Everything is prepared. Confirm to finish the setup and continue to
          paxray.
        </p>
        <div class="setup-actions">
          <Button
            size="small"
            kind="secondary"
            text="Back"
            on:click={() => (currentStep = 2)}
          />
          <Button
            size="small"
            kind="secondary"
            text="Finish setup"
            on:click={setupComplete}
          />
        </div>
      {/if}
    </section>
  </main>
</div>

<Modal
  actions
  buttonSize="sm"
  autowidth
  bind:open={userModalOpen}
  modalHeading={currentUser.id ? `Edit ${currentUser.username}` : "Add User"}
  primaryButtonText="Save"
  primaryButtonVariant="secondary"
  secondaryButtonText="Cancel"
  secondaryButtonAction={closeUserModal}
  primaryButtonAction={saveUser}
  primaryButtonNotAllow={saveUserNotAllowed}
  on:close={closeUserModal}
>
  <div class="setup-user-form">
    <TextInput
      labelText="Name*"
      invalid={!currentUser.username && testUserModal.username}
      invalidText="Please enter a user name."
      placeholder="Enter user name"
      on:change={() => {
        testUserModal.username = true;
      }}
      bind:value={currentUser.username}
    />
    <TextInput
      labelText="E-Mail*"
      invalid={!currentUser.email && testUserModal.email}
      invalidText="Please enter an e-mail address."
      placeholder="e.g. jane.doe@company.com"
      on:change={() => {
        testUserModal.email = true;
      }}
      bind:value={currentUser.email}
    />
    <PasswordInput
      labelText={currentUser.id ? "Password" : "Password*"}
      bind:type={passwordType}
      placeholder="Enter password"
      autocomplete="new-password"
      tooltipAlignment="end"
      invalid={disablePasswordInput}
      invalidText={currentUser.password
        ? "Passwords do not match."
        : "Please add a password."}
      on:change={() => {
        if (!currentUser.passwordConfirm && !currentUser.password) {
          testUserModal.password = false;
          if (currentUser.id) testUserModal.confirmPassword = false;
        }
        testUserModal.save = false;
        testUserModal.password = true;
      }}
      bind:value={currentUser.password}
    >
      <!-- Label ohne das <p> aus PasswordInput, damit es wie bei TextInput aussieht -->
      <svelte:fragment slot="labelText"
        >{currentUser.id ? "Password" : "Password*"}</svelte:fragment
      >
    </PasswordInput>
    <PasswordInput
      labelText={currentUser.id ? "Confirm Password" : "Confirm Password*"}
      bind:type={confirmPasswordType}
      placeholder="Repeat password"
      autocomplete="new-password"
      tooltipAlignment="end"
      invalid={disableConfirmPasswordInput}
      invalidText="Please confirm the password."
      on:change={() => {
        if (!currentUser.passwordConfirm && !currentUser.password) {
          if (currentUser.id) testUserModal.password = false;
          testUserModal.confirmPassword = false;
        }
        testUserModal.save = false;
        testUserModal.confirmPassword = true;
      }}
      bind:value={currentUser.passwordConfirm}
    >
      <svelte:fragment slot="labelText"
        >{currentUser.id
          ? "Confirm Password"
          : "Confirm Password*"}</svelte:fragment
      >
    </PasswordInput>
    <p class="setup-required">* Required</p>
  </div>
</Modal>

<Modal
  actions
  buttonSize="sm"
  autowidth
  bind:open={deleteUserModalOpen}
  modalHeading={`Delete ${userToDelete?.username ?? "user"}?`}
  primaryButtonText="Delete"
  primaryButtonVariant="secondary"
  secondaryButtonText="Cancel"
  secondaryButtonAction={() => {
    userToDelete = null;
    deleteUserModalOpen = false;
  }}
  primaryButtonAction={() => userToDelete && deleteUser(userToDelete.id)}
  on:close={() => {
    userToDelete = null;
    deleteUserModalOpen = false;
  }}
>
  <p>
    The user <b>{userToDelete?.username}</b> will be deleted.<br />
    This action cannot be undone.
  </p>
</Modal>

<style>
  /* Vor dem Login gibt es keine Seitennavigation, der Header startet direkt rechts neben dem Logo */
  .setup {
    --app-side-nav-width: calc(4.5rem + var(--left-gap) + 1rem);
    min-height: 100vh;
    /* Ohne App-Layout fallen Header und Stepper sonst auf IBM Plex Sans zurueck */
    font-family: var(--font-family-inter);
  }

  .setup-logo {
    position: fixed;
    top: 0;
    left: 0;
    z-index: var(--z10);
    display: flex;
    align-items: center;
    width: var(--app-side-nav-width);
    height: var(--app-header-height);
    padding-left: var(--left-gap);
    border-right: var(--bw) solid var(--border-color);
    border-bottom: var(--bw) solid var(--border-color);
    background: var(--pax-white);
  }

  .setup-logo :global(div) {
    width: 4.5rem;
    height: auto;
  }

  .setup-header-progress {
    width: min(38rem, calc(100vw - 10rem));
    min-width: 4rem;
  }

  .setup-content {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    padding: calc(var(--app-header-height) + 2rem) 1rem 2rem;
    /* Dunkle Flaeche passend zum Framework Assistant */
    background: var(--pax-gradient-vertical-new);
  }

  .setup-panel {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
    width: min(36rem, 100%);
    padding: 2rem;
    border-radius: var(--br);
    background: var(--pax-white);
    box-shadow: 0 1rem 2.5rem rgba(28, 28, 32, 0.15);
  }

  h1 {
    font-family: var(--font-family-inter);
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.25;
  }

  .setup-subtitle {
    color: var(--pax-grey);
    line-height: 1.4;
    /* Etwas mehr Luft zwischen Einleitung und Inhalt */
    margin-bottom: var(--control-gap);
  }

  .setup-title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--control-gap);
  }

  .setup-form,
  .setup-user-form {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .setup-field {
    display: flex;
    flex-direction: column;
  }

  .setup-hint {
    margin-top: 0.5rem;
    color: var(--pax-grey);
    font-size: 0.75rem;
  }

  .setup-actions {
    display: flex;
    justify-content: space-between;
    gap: var(--control-gap);
    margin-top: var(--control-gap);
  }

  .setup-actions--end {
    justify-content: flex-end;
  }

  .setup-user-list {
    display: flex;
    flex-direction: column;
    gap: var(--list-item-gap);
  }

  .setup-user {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--control-gap);
    padding: 0.75rem 1rem;
    border: var(--bw) solid var(--border-color);
    border-radius: 0.25rem;
    background: var(--pax-white);
  }

  .setup-user__text {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }

  .setup-user__name {
    font-weight: 600;
  }

  .setup-user__email {
    color: var(--pax-grey);
    font-size: 0.75rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .setup-user__actions {
    display: flex;
    gap: 0.25rem;
  }

  .setup-empty {
    padding: 1rem;
    border: var(--bw) dashed var(--border-color);
    border-radius: 0.25rem;
    color: var(--pax-grey);
    text-align: center;
  }

  .setup-required {
    font-size: 0.75rem;
  }
</style>
